import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from './firebase';
import { collection, addDoc } from 'firebase/firestore';
import { ToastProvider, useToast } from './components/Toast';

import CheckResult from './components/CheckResult';
import ConfirmacionCondicional from './components/ConfirmacionCondicional';
import Home from './components/Home';
import Login from './components/Login';
import './App.css';

function AppContent() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- SINCRONIZACIÓN OFFLINE ---
  useEffect(() => {
    const syncPendingChecklists = async () => {
      let pendingChecklists;
      try {
        const pendingJson = localStorage.getItem('pendingChecklists');
        if (!pendingJson) return;
        pendingChecklists = JSON.parse(pendingJson);
      } catch {
        localStorage.removeItem('pendingChecklists');
        return;
      }

      if (!pendingChecklists || pendingChecklists.length === 0) return;

      toast.info(`Sincronizando ${pendingChecklists.length} checklist(s) guardados sin conexión...`);

      const successfulUploads = [];

      for (const checklist of pendingChecklists) {
        try {
          const { id, ...dataToSave } = checklist;
          dataToSave.fecha = new Date(dataToSave.fechaRealCreacion);
          dataToSave.isSynced = true;
          dataToSave.guardadoOffline = false;
          await addDoc(collection(db, "checklistLivianos"), dataToSave);
          successfulUploads.push(checklist);
        } catch (error) {
          console.error(`Error al sincronizar checklist ${checklist.id}:`, error);
          toast.error("Error durante la sincronización. Se reintentará en la próxima conexión.");
          break;
        }
      }

      if (successfulUploads.length > 0) {
        const remaining = pendingChecklists.filter(p =>
          !successfulUploads.some(s => s.id === p.id)
        );

        if (remaining.length === 0) {
          localStorage.removeItem('pendingChecklists');
          toast.success("Sincronización completada. Todos los checklists están en el servidor.");
        } else {
          localStorage.setItem('pendingChecklists', JSON.stringify(remaining));
          toast.warning(`${remaining.length} checklist(s) aún pendientes de sincronizar.`);
        }
      }
    };

    const handleOnline = () => syncPendingChecklists();
    window.addEventListener('online', handleOnline);
    if (navigator.onLine) syncPendingChecklists();
    return () => window.removeEventListener('online', handleOnline);
  }, [toast]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner spinner-lg"></div>
        <span>Cargando...</span>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={!currentUser ? <Login /> : <Navigate to="/" />} />
        <Route path="/" element={currentUser ? <Home user={currentUser} /> : <Navigate to="/login" />} />
        <Route path="/check-realizado" element={currentUser ? <CheckResult type="success" /> : <Navigate to="/login" />} />
        <Route path="/check-condicional" element={currentUser ? <CheckResult type="conditional" /> : <Navigate to="/login" />} />
        <Route path="/confirmacion-condicional" element={currentUser ? <ConfirmacionCondicional /> : <Navigate to="/login" />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ToastProvider>
      <AppContent />
    </ToastProvider>
  );
}

export default App;
